/**
 * ZPL font → CSS font and dot metrics.
 *
 * Ported from ns-web resources/js/components/common/label-viewer/utils/font-mapper.js
 * Changes:
 *  - ZPL fonts only. EPL fonts, the custom slot fonts (P–V) and the runtime
 *    FontFace loading are not ported.
 *  - Font 0 is Zebra's scalable CG Triumvirate Bold Condensed, not OCR-B as NS
 *    assumed; it now maps to a bold condensed sans-serif. Every Phoenix template
 *    uses ^CF0, so this is the font that matters.
 *  - Font 0 honours the width parameter (w / h horizontal stretch) instead of
 *    ignoring it.
 *  - The calibration constants live in one place (FONT0) so the accuracy check
 *    against Labelary / a real print can tune them.
 */

import type { FontSpec } from './document-model';

/**
 * Bitmap ROM fonts A–H: nearest-neighbour table of measured
 * [specHeight, inkHeight, cellWidth] in dots, measured by NS against Labelary
 * at 203 dpi. Zebra bitmap fonts are defined in dots, so the same table is used
 * at 300 dpi (font E differs there; Phoenix doesn't use it).
 */
const BITMAP_SNAP_TABLE: Readonly<Record<string, ReadonlyArray<readonly [number, number, number]>>> = {
  A: [[12, 7, 6], [16, 14, 12], [20, 14, 12], [28, 21, 18], [40, 28, 24]],
  B: [[12, 11, 9], [16, 11, 9], [20, 22, 18], [28, 33, 27], [40, 44, 36]],
  C: [[12, 14, 12], [16, 14, 12], [20, 14, 12], [28, 28, 24], [40, 28, 24]],
  D: [[12, 14, 12], [16, 14, 12], [20, 14, 12], [28, 28, 24], [40, 28, 24]],
  E: [[12, 20, 14], [16, 20, 19], [20, 20, 19], [28, 20, 19], [40, 20, 19]],
  F: [[12, 21, 14], [20, 21, 16], [28, 21, 16], [40, 42, 32]],
  G: [[12, 24, 13], [16, 32, 18], [28, 48, 32], [40, 48, 39]],
  H: [[16, 21, 18], [20, 21, 18], [28, 21, 18]],
};

const CONDENSED_SANS = '"Arial Narrow","Helvetica Neue Condensed","Roboto Condensed",Arial,sans-serif';

interface CssFace {
  family: string;
  weight: 'normal' | 'bold';
}

const BITMAP_FACES: Readonly<Record<string, CssFace>> = {
  A: { family: CONDENSED_SANS, weight: 'normal' },
  B: { family: '"OCR A Extended","OCR A","Courier New",monospace', weight: 'normal' },
  C: { family: CONDENSED_SANS, weight: 'normal' },
  D: { family: CONDENSED_SANS, weight: 'bold' },
  E: { family: '"OCR B","OCR-B","Courier New",monospace', weight: 'normal' },
  F: { family: CONDENSED_SANS, weight: 'bold' },
  G: { family: CONDENSED_SANS, weight: 'bold' },
  H: { family: '"OCR A Extended","OCR A","Courier New",monospace', weight: 'normal' },
};

/**
 * Font 0 calibration, measured on 85 lines of Labelary output (SV-551 QA gate 1):
 *  - ADVANCE_BY_CLASS: average advance as a fraction of h per character class
 *    (upper case and digits / lower case / anything else); 0.48 overall median.
 *    Used where no canvas is available to measure (measureContent, tests).
 *  - CAP_HEIGHT_PER_HEIGHT: capitals are 0.79 × h tall, with their top exactly
 *    at the ^FO y. The renderer puts the baseline there.
 *  - EM_PER_HEIGHT: a bold condensed sans has caps ≈ 0.716 em, so an em of
 *    ≈ 1.10 × h matches the 0.79 cap height. Provisional until a browser check.
 */
export const FONT0 = {
  face: { family: CONDENSED_SANS, weight: 'bold' } as CssFace,
  EM_PER_HEIGHT: 1.1,
  ADVANCE_PER_HEIGHT: 0.48,
  ADVANCE_BY_CLASS: { upperOrDigit: 0.565, lower: 0.33, other: 0.44 },
  CAP_HEIGHT_PER_HEIGHT: 0.79,
} as const;

/** Font 0 advance of one character as a fraction of the font height. */
export function font0AdvancePerHeight(ch: string): number {
  if (/[\p{Lu}\p{Nd}]/u.test(ch)) return FONT0.ADVANCE_BY_CLASS.upperOrDigit;
  if (/\p{Ll}/u.test(ch)) return FONT0.ADVANCE_BY_CLASS.lower;
  return FONT0.ADVANCE_BY_CLASS.other;
}

/** Zebra's power-on default for ^CF: font A at 9 dots, 5 wide (203 dpi). */
export const DEFAULT_FONT: FontSpec = { name: 'A', height: 9, width: 5 };

export function isBitmapFont(name: string): boolean {
  return name in BITMAP_SNAP_TABLE;
}

/** Fonts the renderer has a deliberate mapping for (others fall back to font 0). */
export function isKnownFont(name: string): boolean {
  return name === '0' || isBitmapFont(name);
}

interface SnappedSize {
  /** Height the printer actually draws, in dots. */
  inkHeight: number;
  /** Fixed advance per character, in dots. */
  cellWidth: number;
}

function snapBitmap(name: string, specH: number): SnappedSize {
  const table = BITMAP_SNAP_TABLE[name];
  let best = table[0];
  for (const row of table) {
    if (Math.abs(row[0] - specH) < Math.abs(best[0] - specH)) best = row;
  }
  return { inkHeight: best[1], cellWidth: best[2] };
}

/** Effective cell height of a font spec in dots (falls back to the default font height). */
export function fontHeightDots(font: FontSpec): number {
  return font.height > 0 ? font.height : DEFAULT_FONT.height;
}

/** Height a line of this font occupies on the label, in dots. */
export function lineBoxHeightDots(font: FontSpec): number {
  const h = fontHeightDots(font);
  return isBitmapFont(font.name) ? Math.max(h, snapBitmap(font.name, h).inkHeight) : h;
}

/** Horizontal stretch applied to font 0 text: w / h, where w = 0 means w = h. */
export function font0WidthFactor(font: FontSpec): number {
  const h = fontHeightDots(font);
  return font.width > 0 ? font.width / h : 1;
}

/**
 * Measured per-character advance for bitmap fonts whose declared ^A width
 * isn't what the printer steps by. Font D: Labelary advances 11.8 dots per
 * character for both ^ADN,18,10 and ^ADN,28,14 (5 lines, QA gate 1), since
 * Zebra scales bitmap fonts in whole multiples of the base cell (10 dots wide
 * for D). Wider declared widths are assumed to step by whole multiples.
 */
const MEASURED_BITMAP_ADVANCE: Readonly<Record<string, { baseWidth: number; advance: number }>> = {
  D: { baseWidth: 10, advance: 11.8 },
};

/** Fixed character advance in dots for bitmap fonts, or null for scalable ones. */
export function bitmapCellWidthDots(font: FontSpec): number | null {
  if (!isBitmapFont(font.name)) return null;
  const measured = MEASURED_BITMAP_ADVANCE[font.name];
  if (measured) {
    const magnification = font.width > 0 ? Math.max(1, Math.round(font.width / measured.baseWidth)) : 1;
    return measured.advance * magnification;
  }
  if (font.width > 0) return font.width;
  return snapBitmap(font.name, fontHeightDots(font)).cellWidth;
}

/** Estimated width of a string in dots without a canvas (for layout estimates). */
export function estimateTextWidthDots(text: string, font: FontSpec): number {
  const cell = bitmapCellWidthDots(font);
  if (cell !== null) return text.length * cell;
  let perHeight = 0;
  for (const ch of text) perHeight += font0AdvancePerHeight(ch);
  return perHeight * fontHeightDots(font) * font0WidthFactor(font);
}

/**
 * Distance in dots from the line top (the ^FO y) to the text baseline, so
 * capitals start at the top as the printer draws them.
 */
export function baselineOffsetDots(font: FontSpec): number {
  const h = fontHeightDots(font);
  if (isBitmapFont(font.name)) return snapBitmap(font.name, h).inkHeight;
  return h * FONT0.CAP_HEIGHT_PER_HEIGHT;
}

/**
 * CSS font shorthand sized in dots (the renderer draws in dot space).
 * Unknown/downloaded fonts are drawn as font 0.
 */
export function cssFontForDots(font: FontSpec): string {
  const h = fontHeightDots(font);
  if (isBitmapFont(font.name)) {
    const face = BITMAP_FACES[font.name];
    return `${face.weight} ${snapBitmap(font.name, h).inkHeight}px ${face.family}`;
  }
  const size = Math.max(1, h * FONT0.EM_PER_HEIGHT);
  return `${FONT0.face.weight} ${size}px ${FONT0.face.family}`;
}
